import { useEffect, useMemo, useRef, useState } from 'react';

import { catalog } from '../api/endpoints.ts';
import type { Category } from '../api/types.ts';
import { useStore } from '../core/store.ts';
import { errorText, useLang, useStrings, type Strings } from '../i18n/index.ts';
import { childrenOf, homeStore, sectionsOf } from '../state/home.ts';
import { openSignIn, pushRoute } from '../state/router.ts';
import { isSignedIn } from '../state/session.ts';
import { ApiError, asApiError } from '../api/client.ts';
import { Chip, cx, ErrorBanner, RemoteImage, ScreenTitle, Skeleton } from '../ui/kit.tsx';
import { Screen } from '../ui/Screen.tsx';
import '../styles/storefront.css';

/** Быстрые фильтры чипами. Покупки и избранное живут с аккаунтом: гостю поднимается вход. */
export function QuickFilters({ strings, order }: { strings: Strings; order: 'home' | 'catalog' }) {
  const personal = (open: () => void) => () => (isSignedIn() ? open() : openSignIn());
  const bought = (
    <Chip key="bought" title={strings.youBought} icon="history" onClick={personal(() => pushRoute({ name: 'list', source: { kind: 'purchases' }, title: strings.youBought }))} />
  );
  const favorites = (
    <Chip key="favorites" title={strings.favoritesTitle} icon="heartFill" onClick={personal(() => pushRoute({ name: 'list', source: { kind: 'favorites' }, title: strings.favoritesTitle }))} />
  );
  const deals = <Chip key="deals" title={strings.deals} icon="percent" onClick={() => pushRoute({ name: 'list', source: { kind: 'deals' }, title: strings.deals })} />;
  const fresh = <Chip key="new" title={strings.newItems} icon="sparkles" onClick={() => pushRoute({ name: 'list', source: { kind: 'newItems' }, title: strings.newItems })} />;
  return <div className="lane">{order === 'home' ? [bought, favorites, deals, fresh] : [deals, fresh, favorites, bought]}</div>;
}

/** Каталог: разделы с плитками подкатегорий на белых карточках, сверху чипы быстрых фильтров. */
export function CatalogScreen() {
  const strings = useStrings();
  const lang = useLang();
  const categories = useStore(homeStore).categories;
  const [error, setError] = useState<ApiError | null>(null);

  const load = () => {
    setError(null);
    catalog
      .categories(lang)
      .then((response) => homeStore.set((state) => ({ ...state, categories: response.items })))
      .catch((cause: unknown) => setError(asApiError(cause)));
  };
  // Категории общие с главной; каталог перечитывает их на своём языке.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [lang]);

  // Раздел без подкатегорий не показывается.
  const sections = useMemo(
    () => sectionsOf(categories).map((section) => ({ section, tiles: childrenOf(categories, section) })).filter((entry) => entry.tiles.length > 0),
    [categories],
  );

  return (
    <Screen>
      <div className="page stack" style={{ gap: 26, paddingTop: 8 }}>
        <div style={{ marginBottom: -10 }}>
          <ScreenTitle text={strings.catalogTab} />
        </div>
        <QuickFilters strings={strings} order="catalog" />

        {error && categories.length === 0 ? (
          <div className="gutter">
            <ErrorBanner text={errorText(strings, error)} onRetry={load} />
          </div>
        ) : categories.length === 0 ? (
          <div className="stack gutter" style={{ gap: 26 }}>
            <Skeleton height={150} />
            <Skeleton height={150} />
            <Skeleton height={150} />
          </div>
        ) : sections.length === 0 ? (
          // Категории без разделов: одна общая сетка.
          <CatalogSection title={strings.catalogTab} tiles={[...categories].sort((a, b) => a.sortOrder - b.sortOrder)} />
        ) : (
          <div className="catalog-sections">
            {sections.map(({ section, tiles }) => (
              <CatalogSection key={section.id} title={section.name} tiles={tiles} />
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}

function CatalogSection({ title, tiles }: { title: string; tiles: Category[] }) {
  return (
    <section className="stack" style={{ gap: 12 }}>
      <h2 className="t-display catalog-section-title">{title}</h2>
      <MosaicGrid tiles={tiles} />
    </section>
  );
}

/**
 * Мозаика плиток раздела: ряд высоких плиток, затем ряд пониже на плитку шире. На телефоне
 * это 2 и 3, на широком экране колонок больше (одна на каждые 200 px ширины).
 */
function MosaicGrid({ tiles }: { tiles: Category[] }) {
  const element = useRef<HTMLDivElement>(null);
  const [base, setBase] = useState(2);

  useEffect(() => {
    const node = element.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBase(Math.max(2, Math.floor(entry.contentRect.width / 200)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Разбиение: base, base + 1, base… Остаток в base + 1 берётся одним рядом, чтобы не
  // оставлять одинокую плитку.
  const rows: { tiles: Category[]; columns: number }[] = [];
  let rest = tiles;
  let wide = true;
  while (rest.length > 0) {
    const columns = rest.length === base + 1 ? base + 1 : wide ? base : base + 1;
    rows.push({ tiles: rest.slice(0, columns), columns });
    rest = rest.slice(columns);
    wide = !wide;
  }

  return (
    <div className="mosaic" ref={element}>
      {rows.map((row, index) => {
        // Единственная плитка ряда занимает его целиком; на телефоне растягивается и неполный
        // ряд, на широком экране хвост оставляет колонки пустыми, иначе плитки вырастают вдвое.
        const columns = row.tiles.length === 1 || base === 2 ? row.tiles.length : row.columns;
        const tall = row.columns === base;
        return (
          <div key={index} className="mosaic-row" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {row.tiles.map((category) => (
              <button
                key={category.id}
                type="button"
                className={cx('mosaic-tile', tall && 'is-tall')}
                onClick={() => pushRoute({ name: 'category', category })}
              >
                <span className="mosaic-name">{category.name}</span>
                <span className="mosaic-image">
                  <RemoteImage src={category.imageUrl} />
                </span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}
